/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Checkbox } from "@octopusdeploy/design-system-components";
import type { EnvironmentResource, LifecycleResource, PhaseResource } from "@octopusdeploy/octopus-server-client";
import { DeployLatestReleaseActionResource } from "@octopusdeploy/octopus-server-client";
import { isEqual, compact, uniqBy } from "lodash";
import * as React from "react";
import { EnvironmentChip, ChipIcon } from "~/components/Chips";
import LookupResourceChipComponent from "~/components/LookupResourceChip";
import { ExpandableFormSection, Summary } from "~/components/form";
import EnvironmentSelect from "~/components/form/EnvironmentSelect/EnvironmentSelect";
interface DeployLatestReleaseActionEditorProps {
    action: DeployLatestReleaseActionResource;
    allEnvironments: EnvironmentResource[];
    lifecycle: LifecycleResource;
    onActionChange(action: DeployLatestReleaseActionResource): void;
}
interface DeployLatestReleaseActionEditorState {
    sourceEnvironmentIds: string[];
    destinationEnvironmentId: string;
    shouldRedeployWhenReleaseIsCurrent: boolean;
}
export class DeployLatestReleaseActionEditor extends React.Component<DeployLatestReleaseActionEditorProps, DeployLatestReleaseActionEditorState> {
    constructor(props: DeployLatestReleaseActionEditorProps) {
        super(props);
        this.state = this.initState(this.props.action);
    }
    //eslint-disable-next-line react/no-unsafe
    UNSAFE_componentWillReceiveProps(newProps: DeployLatestReleaseActionEditorProps) {
        const sourceEnvironmentsAreTheSame = isEqual(this.props.action.SourceEnvironmentIds || [], newProps.action.SourceEnvironmentIds || []);
        const destinationEnvironmentsAreTheSame = this.props.action.DestinationEnvironmentId === newProps.action.DestinationEnvironmentId;
        if (!sourceEnvironmentsAreTheSame || !destinationEnvironmentsAreTheSame) {
            this.setState(this.initState(newProps.action));
        }
        const lifecycleChanged = this.props.action.ChannelId !== newProps.action.ChannelId || this.props.lifecycle.Id !== newProps.lifecycle.Id;
        if (lifecycleChanged) {
            this.clearEnvironmentsMissingFromLifecycle(newProps);
        }
    }
    render() {
        const { lifecycle, allEnvironments } = this.props;
        // Derived on every render rather than cached, so the dropdowns are correct on the very first
        // render and can never hold options from a previous lifecycle or selection.
        const sourceEnvironments = getSourceEnvironmentOptions(lifecycle, allEnvironments, this.state.destinationEnvironmentId);
        const destinationEnvironments = getDestinationEnvironmentOptions(lifecycle, allEnvironments, this.getSourceEnvironmentFromState());
        return (<div>
                <ExpandableFormSection errorKey="SourceEnvironment" title="Source environment" focusOnExpandAll summary={this.buildSourceEnvironmentSummary()} help="The environment to use when selecting the release to deploy from.">
                    <EnvironmentSelect allowClear={true} environments={sourceEnvironments} value={this.getSourceEnvironmentFromState()!} onChange={this.onSourceEnvironmentChange}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="DestinationEnvironment" title="Destination environment" focusOnExpandAll summary={this.buildDestinationEnvironmentSummary()} help="The environment to deploy the selected release to.">
                    <EnvironmentSelect allowClear={true} environments={destinationEnvironments} value={this.state.destinationEnvironmentId} onChange={this.onDestinationEnvironmentChange}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Redeploy" title="Re-deploy" summary={this.state.shouldRedeployWhenReleaseIsCurrent
                ? Summary.default("Re-deploy latest release to destination environment even if already up-to-date")
                : Summary.summary("Do not re-deploy latest release to destination environment if already up-to-date")} help="Choose whether Octopus should re-deploy latest release to destination environment if it is already up-to-date">
                    <Checkbox label="Re-deploy" value={this.state.shouldRedeployWhenReleaseIsCurrent} onChange={this.onShouldRedeployChange}/>
                </ExpandableFormSection>
            </div>);
    }
    initState(value?: DeployLatestReleaseActionResource) {
        const action = value || new DeployLatestReleaseActionResource();
        return {
            sourceEnvironmentIds: action.SourceEnvironmentIds,
            destinationEnvironmentId: action.DestinationEnvironmentId,
            shouldRedeployWhenReleaseIsCurrent: action.ShouldRedeployWhenReleaseIsCurrent,
        };
    }
    private environmentChip = (id: string) => {
        const LookupEnvironmentChip = LookupResourceChipComponent<EnvironmentResource>();
        return <LookupEnvironmentChip lookupCollection={this.props.allEnvironments} key={id} lookupId={id} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>;
    };
    private buildSourceEnvironmentSummary = () => {
        const envs = this.state.sourceEnvironmentIds || [];
        return envs.length > 0
            ? Summary.summary(<span>
                      Latest release in the {envs.map((e) => this.environmentChip(e))} environment{envs.length === 1 ? "" : "s"} will be deployed
                  </span>)
            : Summary.placeholder("No source environment selected");
    };
    private buildDestinationEnvironmentSummary = () => {
        return this.state.destinationEnvironmentId
            ? Summary.summary(<span>Latest release will be deployed to the {this.environmentChip(this.state.destinationEnvironmentId)} environment</span>)
            : Summary.placeholder("No destination environment selected");
    };
    private onSourceEnvironmentChange = (environmentId: string | undefined) => {
        this.setState({
            sourceEnvironmentIds: environmentId ? [environmentId!] : [],
        }, () => this.raiseChange());
    };
    private onDestinationEnvironmentChange = (environmentId: string | undefined) => {
        this.setState({
            destinationEnvironmentId: environmentId!,
        }, () => this.raiseChange());
    };
    private onShouldRedeployChange = (shouldRedeployWhenMachineHasBeenDeployedTo: boolean) => {
        this.setState({
            shouldRedeployWhenReleaseIsCurrent: shouldRedeployWhenMachineHasBeenDeployedTo,
        }, () => this.raiseChange());
    };
    private raiseChange = () => {
        this.props.onActionChange({
            ...this.props.action,
            SourceEnvironmentIds: this.state.sourceEnvironmentIds,
            DestinationEnvironmentId: this.state.destinationEnvironmentId,
            ShouldRedeployWhenReleaseIsCurrent: this.state.shouldRedeployWhenReleaseIsCurrent,
        });
    };
    // When the channel (and therefore the lifecycle) changes, any environment that isn't part of the
    // new lifecycle can no longer be deployed to or from, so drop it from the selection.
    private clearEnvironmentsMissingFromLifecycle(newProps: DeployLatestReleaseActionEditorProps) {
        const lifecycleEnvironments = getLifecycleEnvironments(newProps.lifecycle, newProps.allEnvironments);
        const isInLifecycle = (environmentId: string) => lifecycleEnvironments.some((e) => e.Id === environmentId);
        const sourceEnvironmentId = newProps.action.SourceEnvironmentIds ? newProps.action.SourceEnvironmentIds[0] : null;
        const destinationEnvironmentId = newProps.action.DestinationEnvironmentId;
        const sourceIsMissing = !!sourceEnvironmentId && !isInLifecycle(sourceEnvironmentId);
        const destinationIsMissing = !!destinationEnvironmentId && !isInLifecycle(destinationEnvironmentId);
        if (!sourceIsMissing && !destinationIsMissing) {
            return;
        }
        this.setState({
            sourceEnvironmentIds: sourceIsMissing ? [] : compact([sourceEnvironmentId]),
            destinationEnvironmentId: destinationIsMissing ? null! : destinationEnvironmentId,
        }, () => this.raiseChange());
    }
    private getSourceEnvironmentFromState() {
        return this.state.sourceEnvironmentIds ? this.state.sourceEnvironmentIds[0] : null;
    }
    static displayName = "DeployLatestReleaseActionEditor";
}
// Every environment in the lifecycle, in any phase.
//
// Deploy latest release promotes an existing release, so any environment in the lifecycle is a
// candidate for both source and destination. This deliberately differs from Deploy new release and
// Deploy latest release to environment, which can only target the first (non-optional) phase.
export function getLifecycleEnvironments(lifecycle: LifecycleResource, allEnvironments: EnvironmentResource[]): EnvironmentResource[] {
    return toAccessibleEnvironments(getPhases(lifecycle).flatMap(getPhaseEnvironmentIds), allEnvironments);
}
// The environments a release in `sourceEnvironmentId` can be promoted to.
export function getDestinationEnvironmentOptions(lifecycle: LifecycleResource, allEnvironments: EnvironmentResource[], sourceEnvironmentId: string | null | undefined): EnvironmentResource[] {
    return getEnvironmentOptions(lifecycle, allEnvironments, sourceEnvironmentId, 1);
}
// The environments a release can be promoted from in order to reach `destinationEnvironmentId`.
export function getSourceEnvironmentOptions(lifecycle: LifecycleResource, allEnvironments: EnvironmentResource[], destinationEnvironmentId: string | null | undefined): EnvironmentResource[] {
    return getEnvironmentOptions(lifecycle, allEnvironments, destinationEnvironmentId, -1);
}
function getEnvironmentOptions(lifecycle: LifecycleResource, allEnvironments: EnvironmentResource[], selectedEnvironmentId: string | null | undefined, direction: 1 | -1): EnvironmentResource[] {
    if (!selectedEnvironmentId) {
        return getLifecycleEnvironments(lifecycle, allEnvironments);
    }
    const phases = getPhases(lifecycle);
    const selectedPhaseIndex = phases.findIndex((p) => getPhaseEnvironmentIds(p).includes(selectedEnvironmentId));
    if (selectedPhaseIndex < 0) {
        // The selected environment isn't in this lifecycle, or the user can't see the phase it lives
        // in, so there is nothing to narrow the options against.
        return getLifecycleEnvironments(lifecycle, allEnvironments);
    }
    const environmentIds = [...getPhaseEnvironmentIds(phases[selectedPhaseIndex]), ...getEnvironmentIdsFromAdjacentPhases(phases, selectedPhaseIndex, direction)];
    return toAccessibleEnvironments(environmentIds, allEnvironments);
}
// Environments from the phase adjacent to `index` in `direction`, continuing past that phase while
// the phases are optional - the same reachability rule a manual deployment follows.
function getEnvironmentIdsFromAdjacentPhases(phases: PhaseResource[], index: number, direction: 1 | -1): string[] {
    const adjacentIndex = index + direction;
    const adjacentPhase = phases[adjacentIndex];
    if (!adjacentPhase) {
        return [];
    }
    const phasesBeyond = adjacentPhase.IsOptionalPhase ? getEnvironmentIdsFromAdjacentPhases(phases, adjacentIndex, direction) : [];
    return [...phasesBeyond, ...getPhaseEnvironmentIds(adjacentPhase)];
}
function getPhases(lifecycle: LifecycleResource): PhaseResource[] {
    return lifecycle?.Phases ?? [];
}
function getPhaseEnvironmentIds(phase: PhaseResource): string[] {
    return [...phase.AutomaticDeploymentTargets, ...phase.OptionalDeploymentTargets];
}
// allEnvironments is permission filtered by the server, so a lifecycle phase can reference
// environments the current user isn't allowed to see. Those are dropped here.
function toAccessibleEnvironments(environmentIds: string[], allEnvironments: EnvironmentResource[]): EnvironmentResource[] {
    return uniqBy(compact(environmentIds.map((environmentId) => allEnvironments.find((e) => e.Id === environmentId))), (e) => e.Id);
}
